import { getQueryString, updateUrlParameter } from '@umu/umu-dom';
import { openWindow } from '@umu/umu-dom';
import { isDingding } from '@umu/umu-ua';
import _get from 'lodash/get';

type TMiniAppEnv = 'dd' | 'wx';
/**
 * 当前页面启动来源是否为课程小程序
 */
export function isFromCourseMiniApp(): boolean | TMiniAppEnv {
    // url 中带参数时以 url 中的为准
    const isCourseMiniApp = _get(window, 'pageData.config.is_course_mini_app', false);
    if (!isCourseMiniApp) {
        return false;
    }
    if (isDingding) {
        return 'dd';
    } else {
        return 'wx';
    }
}
export function isFromMideaMiniApp(): boolean {
    // url 中带参数时以 url 中的为准
    const isIn = _get(window, 'pageData.config.is_in_midea_mini_app', false);
    if (!isIn) {
        return false;
    }
    return true;
}
interface IMiniAppApiOptions {
    success: (res?: any) => void;
    fail: (res?: any) => void;
    complete: (res?: any) => void;
}
export function addMiniAppOriginToUrl(url: string) {
    if (isFromCourseMiniApp()) {
        const isFromMiniCourse = getQueryString('fromMiniCourseOrigin');
        if (isFromMiniCourse) {
            // 传递参数
            return updateUrlParameter(url, 'fromMiniCourseOrigin', isFromMiniCourse);
        }
    }
    return url;
}
/**
 * 统一钉钉和微信小程序的api
 * 需要先判断当前页面启动来源是否为课程小程序
 */
export function getMiniAppSdk() {
    const env = isFromCourseMiniApp();
    const sdkMap = {
        dd: (window as any).dd as any,
        wx: (window as any).wx?.miniProgram as any,
    };

    const envSdk = sdkMap[env as TMiniAppEnv];
    const navigateTo = (url: string, options?: IMiniAppApiOptions) => {
        return envSdk.navigateTo({ url, ...(options || {}) });
    };
    const postMessage = (data: any) => {
        return envSdk.postMessage({ data });
    };
    const navigateBack = (delta?: number) => {
        return envSdk.navigateBack(delta);
    };
    const redirectTo = (url: string, options?: IMiniAppApiOptions) => {
        return envSdk.redirectTo({ url, ...(options || {}) });
    };
    return {
        navigateTo,
        navigateBack,
        postMessage,
        redirectTo,
    };
}
export const isGroupUrl = (href: string) => {
    const groupUrlRegex = /course\/?\?groupId=(\d+)/;
    const match1 = href.match(groupUrlRegex);
    if (match1) {
        return match1[1];
    }

    return false;
};

/**
 * 检查给定的 URL 是否为移动端主页面路径
 *
 * 此函数用于判断一个 URL 的路径部分是否为 '/main'，通常用于
 * 识别移动端的主页面。函数具有健壮的错误处理机制，对于无效
 * 输入会返回 false 而不会抛出异常。
 *
 * @param url - 要检查的 URL 字符串
 * @returns 如果 URL 的 pathname 为 '/main' 则返回 true，否则返回 false
 *
 * @example
 * ```typescript
 * isMobileMainUrl('https://example.com/main') // true
 * isMobileMainUrl('https://example.com/home') // false
 * isMobileMainUrl('invalid-url')              // false
 * isMobileMainUrl('')                         // false
 * isMobileMainUrl(null)                       // false
 * ```
 *
 * @since 1.0.0
 */
export const isMobileMainUrl = (url: string): boolean => {
    // 参数检查：确保 url 是有效的字符串
    // 这里同时检查 null、undefined 和类型安全
    if (!url || typeof url !== 'string') {
        return false;
    }

    // 去除首尾空白字符，避免因空格导致的误判
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
        return false;
    }

    try {
        // 安全创建 URL 对象，这里可能抛出 TypeError
        const urlObj = new URL(trimmedUrl);

        // 检查 pathname 是否为 /main（移动端主页面的标准路径）
        return urlObj.pathname === '/main';
    } catch (error) {
        // URL 格式错误时返回 false，而不是抛出异常
        // 这样确保函数的行为一致性，调用方无需处理异常
        console.warn('isMobileMainUrl: Invalid URL format:', url, error);
        return false;
    }
};
/**
 * 从m端页面打开课程，处理在课程小程序中打开的特殊场景
 * https://app.asana.com/0/1201311184760466/1208099431983400/f
 */
export function openCourseInMp(
    params: { groupId?: number | string; url?: string; options?: any; target?: '_blank' | '_self' },
    customAction?: () => void,
) {
    const groupId = params.groupId || isGroupUrl(params.url || '');
    if (isFromCourseMiniApp() && groupId) {
        const mpUrl = '/pages/courseHome/index?groupId=' + groupId;
        getMiniAppSdk().navigateTo(mpUrl);
    } else {
        if (isFromMideaMiniApp() && params.url) {
            openUrlInMp(params.url);
        } else {
            if (customAction) {
                customAction();
            } else {
                params.url && openWindow(params.url, params.target);
            }
        }
    }
}

export const getCurrentUmuId = () => {
    return String(
        _get(window, 'pageData.data.user_info.umu_id') ||
            _get(window, 'pageData.data.userInfo.umu_id') ||
            _get(window, 'pageData.data.teacher_info.umu_id') ||
            _get(window, 'pageData.data.teacherInfo.umu_id') ||
            '0',
    );
};

export async function openUrlInMp(url: string) {
    // 判断是否是移动端主页面跳转移动端主页面
    const isMobileMainToMobileMain = isMobileMainUrl(window.location.href) && isMobileMainUrl(url);
    if (isFromMideaMiniApp() && !isMobileMainToMobileMain) {
        // 判断 url 是否是完整的 url
        if (!/^https?:\/\//.test(url)) {
            url = `${window.location.origin}${url}`;
        }
        const groupId = isGroupUrl(url);
        if (groupId) {
            getMiniAppSdk().navigateTo(`/pages/courseHome/index?groupId=${groupId}`);
        } else {
            getMiniAppSdk().navigateTo(`/pages/staticWebview/index?url=${encodeURIComponent(url)}`);
        }
    } else {
        window.location.href = url;
    }
}

const getLogoSize = (aspectRatio: number) => {
    const maxWidth = 80;
    const maxHeight = 38;
    // 计算图片显示的宽高
    let imgWidth = maxWidth;
    let imgHeight = maxWidth / aspectRatio;
    if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = maxHeight * aspectRatio;
    }
    return { width: `${Math.floor(imgWidth)}px`, height: `${Math.floor(imgHeight)}px` };
};

export const getLogoStyle = (url: string, callback: (style: any) => void) => {
    const img = new Image();
    img.onload = function () {
        if (!img.width || !img.height) {
            return;
        }
        const logoSpectRatio = img.width / img.height;
        callback(getLogoSize(logoSpectRatio));
    };

    img.src = url;
};
